// components/EditNormalDatePickerStep.jsx
import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import moment from "moment-jalaali";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Lock,
  CheckCircle,
  AlertCircle,
  Loader2,
  ArrowRight,
  ArrowLeft,
  XCircle,
  User,
} from "lucide-react";
import { toast } from "react-toastify";

const persianMonths = [
  { id: 1, name: "فروردین", days: 31 },
  { id: 2, name: "اردیبهشت", days: 31 },
  { id: 3, name: "خرداد", days: 31 },
  { id: 4, name: "تیر", days: 31 },
  { id: 5, name: "مرداد", days: 31 },
  { id: 6, name: "شهریور", days: 31 },
  { id: 7, name: "مهر", days: 30 },
  { id: 8, name: "آبان", days: 30 },
  { id: 9, name: "آذر", days: 30 },
  { id: 10, name: "دی", days: 30 },
  { id: 11, name: "بهمن", days: 30 },
  { id: 12, name: "اسفند", days: 29 },
];

const weekDays = ["ش", "ی", "د", "س", "چ", "پ", "ج"];

const reasonLabelMap = {
  SALON_HOLIDAY: "تعطیلی سالن",
  FULLY_BOOKED: "ظرفیت تکمیل",
  STAFF_NOT_WORKING: "هیچ نیروی کاری کار نمی‌کند",
};

const isLeapYear = (year) => {
  const breaks = [1, 5, 9, 13, 17, 22, 26, 30];
  return breaks.includes(year % 33);
};

const getDaysInMonth = (month, year) => {
  if (!month || month < 1 || month > 12) return 30;
  if (month === 12 && isLeapYear(year)) return 30;
  return persianMonths[month - 1].days;
};

const toDateOnly = (value) => (value ? String(value).split("T")[0] : "");

const EditNormalDatePickerStep = ({
  axiosAdmin,
  selectedService,
  selectedType,
  appointmentDateTime,
  appointmentStaff,
  onDateTimeChange,
}) => {
  // ----- state for availability check -----
  const [availabilityLoading, setAvailabilityLoading] = useState(true);
  const [availabilityError, setAvailabilityError] = useState("");
  const [availabilityData, setAvailabilityData] = useState(null);

  // ----- internal selected date & time & staff -----
  const [internalSelectedDate, setInternalSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);

  // ----- day detail state -----
  const [dayDetail, setDayDetail] = useState(null);
  const [fetchingDay, setFetchingDay] = useState(false);

  // ----- calendar navigation -----
  const [selectedYear, setSelectedYear] = useState(moment().jYear());
  const [selectedMonth, setSelectedMonth] = useState(moment().jMonth() + 1);

  const containerRef = useRef(null);
  const timeSectionRef = useRef(null);

  // Refs for stable callback and preventing duplicate notifications
  const onDateTimeChangeRef = useRef(onDateTimeChange);
  const hasNotifiedInitial = useRef(false);

  // Keep the ref in sync with the latest callback
  useEffect(() => {
    onDateTimeChangeRef.current = onDateTimeChange;
  }, [onDateTimeChange]);

  const serviceId = selectedService?._id;
  const origDateStr = appointmentDateTime?.date;
  const origTime = appointmentDateTime?.time;
  const origStaff = appointmentStaff;

  // ----- initial jalaali month/year from original appointment date -----
  useEffect(() => {
    if (origDateStr) {
      const m = moment(origDateStr, "YYYY-MM-DD");
      if (m.isValid()) {
        setSelectedYear(m.jYear());
        setSelectedMonth(m.jMonth() + 1);
      }
    }
  }, [origDateStr]);

  // ----- fetch availability data on mount -----
  useEffect(() => {
    if (!selectedService || !selectedType) return;
    const fetchAvailability = async () => {
      setAvailabilityLoading(true);
      setAvailabilityError("");
      try {
        const res = await axiosAdmin.post("/api/admin/appointment/check-availability-normal", {
          serviceId: selectedService._id,
          type: selectedType,
          days: 180,
        });
        setAvailabilityData(res.data);
      } catch (err) {
        const msg = err?.response?.data?.message || "خطا در بررسی ظرفیت رزرو عادی";
        setAvailabilityError(msg);
        toast.error(msg);
      } finally {
        setAvailabilityLoading(false);
      }
    };
    fetchAvailability();
  }, [axiosAdmin, selectedService, selectedType]);

  // ----- build maps from availability data -----
  const availableMap = useMemo(() => {
    const map = {};
    (availabilityData?.availability?.availableDates || []).forEach((item) => {
      map[toDateOnly(item.date)] = item;
    });
    return map;
  }, [availabilityData]);

  const unavailableMap = useMemo(() => {
    const map = {};
    (availabilityData?.availability?.unavailableDates || []).forEach((item) => {
      map[toDateOnly(item.date)] = item;
    });
    return map;
  }, [availabilityData]);

  const serverToday = availabilityData?.dateNowServer?.today || moment().format("YYYY-MM-DD");

  // ----- auto-select the original date after availability loads -----
  useEffect(() => {
    if (!origDateStr || !availabilityData) return;
    const jalaaliDate = moment(origDateStr, "YYYY-MM-DD").format("jYYYY/jMM/jDD");
    setInternalSelectedDate({
      date: origDateStr,
      jalaaliDate,
    });
    // Reset notification flag when availability data changes (e.g., remount)
    hasNotifiedInitial.current = false;
  }, [origDateStr, availabilityData]);

  // ----- fetch day availability whenever selected date changes -----
  useEffect(() => {
    if (!internalSelectedDate || !axiosAdmin || !serviceId || !selectedType) return;
    const fetchDay = async () => {
      setFetchingDay(true);
      setDayDetail(null);
      try {
        const res = await axiosAdmin.get("/api/admin/appointment/day-availability-normal", {
          params: {
            serviceId,
            type: selectedType,
            date: internalSelectedDate.date,
          },
        });
        setDayDetail(res.data);
      } catch (err) {
        toast.error("خطا در دریافت جزئیات روز");
      } finally {
        setFetchingDay(false);
      }
    };
    fetchDay();
  }, [internalSelectedDate, axiosAdmin, serviceId, selectedType]);

  // ----- augment time slots: make original time always available on original date -----
  const augmentedTimeSlots = useMemo(() => {
    if (!dayDetail || !dayDetail.timeSlots || !internalSelectedDate?.date) return [];

    const isOriginalDate = internalSelectedDate.date === origDateStr;

    return dayDetail.timeSlots.map((slot) => {
      const isOriginalTime = isOriginalDate && slot.time === origTime;

      let available = slot.available;
      let staffs = [...(slot.staffs || [])];

      if (isOriginalTime) {
        available = true;
        if (origStaff && !staffs.find((s) => s._id === origStaff._id)) {
          staffs.push({
            _id: origStaff._id,
            displayName: origStaff.displayForAdmin || origStaff.displayForUser,
            phone: origStaff.phone || "",
            appointmentCount: origStaff.appointmentCount || 0,
          });
        }
      }

      return {
        time: slot.time,
        available,
        staffs,
      };
    });
  }, [dayDetail, internalSelectedDate, origDateStr, origTime, origStaff]);

  // ----- auto-select original time when on original date and day detail loaded -----
  useEffect(() => {
    if (
      origTime &&
      internalSelectedDate?.date === origDateStr &&
      augmentedTimeSlots.length > 0
    ) {
      const slot = augmentedTimeSlots.find((s) => s.time === origTime);
      if (slot && slot.available) {
        setSelectedTime(origTime);
        const staff = pickStaffForTime(slot.staffs);
        setSelectedStaff(staff);

        // Notify parent only once for the initial selection
        if (!hasNotifiedInitial.current && onDateTimeChangeRef.current) {
          onDateTimeChangeRef.current({
            date: internalSelectedDate.date,
            jalaaliDate: internalSelectedDate.jalaaliDate,
            time: origTime,
            staff,
          });
          hasNotifiedInitial.current = true;
        }
      } else {
        setSelectedTime(null);
        setSelectedStaff(null);
      }
    } else {
      setSelectedTime(null);
      setSelectedStaff(null);
    }
    // Note: onDateTimeChange is not in the dependency array; we use the ref instead.
  }, [augmentedTimeSlots, internalSelectedDate, origDateStr, origTime]);

  // ----- calendar generation -----
  const calendarDays = useMemo(() => {
    if (!selectedMonth || !selectedYear) return [];
    const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
    const firstDayOfMonth = moment(`${selectedYear}/${selectedMonth}/1`, "jYYYY/jM/jD");
    const startDayOfWeek = firstDayOfMonth.day() + 1;
    const days = [];
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const jalaaliDate = moment(`${selectedYear}/${selectedMonth}/${day}`, "jYYYY/jM/jD").format("jYYYY/jMM/jDD");
      const gregorianDate = moment(`${selectedYear}/${selectedMonth}/${day}`, "jYYYY/jM/jD").format("YYYY-MM-DD");
      const availableItem = availableMap[gregorianDate];
      const unavailableItem = unavailableMap[gregorianDate];

      const isOriginalDate = gregorianDate === origDateStr;
      const isAvailable = isOriginalDate ? true : Boolean(availableItem);

      days.push({
        day,
        jalaaliDate,
        gregorianDate,
        isToday: gregorianDate === serverToday,
        isAvailable,
        isUnavailable: !isAvailable && Boolean(unavailableItem),
        isSelected: internalSelectedDate?.date === gregorianDate,
        availableItem: isAvailable ? availableItem || {} : null,
        unavailableItem: !isAvailable ? unavailableItem : null,
      });
    }
    return days;
  }, [selectedMonth, selectedYear, availableMap, unavailableMap, serverToday, internalSelectedDate, origDateStr]);

  const currentMonthName = persianMonths[selectedMonth - 1]?.name || "نامشخص";

  const changeMonth = (dir) => {
    if (dir === "next") {
      if (selectedMonth === 12) {
        setSelectedMonth(1);
        setSelectedYear((y) => y + 1);
      } else {
        setSelectedMonth((m) => m + 1);
      }
    } else {
      if (selectedMonth === 1) {
        setSelectedMonth(12);
        setSelectedYear((y) => y - 1);
      } else {
        setSelectedMonth((m) => m - 1);
      }
    }
  };

  // ----- pick staff with lowest appointment count -----
  const pickStaffForTime = (staffs) => {
    if (!staffs || staffs.length === 0) return null;
    const minCount = Math.min(...staffs.map((s) => s.appointmentCount));
    const candidates = staffs.filter((s) => s.appointmentCount === minCount);
    const chosen = candidates[Math.floor(Math.random() * candidates.length)];
    return chosen;
  };

  // ----- handle date selection -----
  const handleSelectDay = (dateObj) => {
    if (!dateObj || !dateObj.isAvailable) return;
    const payload = {
      date: dateObj.gregorianDate,
      jalaaliDate: dateObj.jalaaliDate,
    };
    setInternalSelectedDate(payload);
    setSelectedTime(null);
    setSelectedStaff(null);
    // Reset notification flag when date changes
    hasNotifiedInitial.current = false;

    setTimeout(() => {
      timeSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  // ----- handle time selection -----
  const handleTimeSelect = (time) => {
    const slot = augmentedTimeSlots.find((s) => s.time === time);
    if (!slot || !slot.available) return;
    const staff = pickStaffForTime(slot.staffs);
    setSelectedTime(time);
    setSelectedStaff(staff);
    if (internalSelectedDate && onDateTimeChangeRef.current) {
      onDateTimeChangeRef.current({
        date: internalSelectedDate.date,
        jalaaliDate: internalSelectedDate.jalaaliDate,
        time,
        staff,
      });
    }
  };

  const getUnavailableReasonText = (dateObj) => {
    const reasons = dateObj?.unavailableItem?.reasons || [];
    if (!reasons.length) return "غیرقابل رزرو";
    return reasons.map((r) => reasonLabelMap[r.code] || r.message).join("، ");
  };

  // ----- render -----
  if (availabilityLoading) {
    return (
      <div className="min-h-[500px] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
          <h3 className="text-lg font-bold text-slate-800 mb-2">در حال بررسی ظرفیت رزرو</h3>
          <p className="text-sm text-slate-500">لطفاً چند لحظه صبر کنید...</p>
        </div>
      </div>
    );
  }

  if (availabilityError) {
    return (
      <div className="min-h-[500px] flex items-center justify-center">
        <div className="max-w-md w-full bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h3 className="text-lg font-bold text-red-700 mb-2">خطا در دریافت ظرفیت</h3>
          <p className="text-sm text-red-600 leading-7">{availabilityError}</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-purple-600 via-purple-600 to-fuchsia-600 rounded-2xl p-5 shadow-lg shadow-purple-500/25 relative overflow-hidden">
        <div className="absolute -top-10 -left-10 w-32 h-32 rounded-full bg-white/10" />
        <div className="absolute -bottom-16 -right-16 w-44 h-44 rounded-full bg-white/10" />
        <div className="relative z-10 flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Calendar className="w-6 h-6 text-white" />
            <h3 className="text-xl font-bold text-white">ویرایش تاریخ و ساعت عادی</h3>
          </div>
          <p className="text-purple-100 text-sm">
            تاریخ جدید را انتخاب کنید. تاریخ فعلی قرار به‌صورت پیش‌فرض انتخاب شده است.
          </p>
        </div>
      </div>

      {/* Calendar */}
      <div className="bg-white max-w-2xl mx-auto border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        {/* Calendar Header */}
        <div className="bg-slate-900 px-4 md:px-5 py-4 flex items-center justify-between">
          <button
            onClick={() => changeMonth("prev")}
            className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center"
          >
            <ChevronRight className="w-5 h-5 text-white" />
          </button>
          <div className="text-center">
            <h3 className="text-lg md:text-xl font-black text-white">
              {currentMonthName} {selectedYear}
            </h3>
          </div>
          <button
            onClick={() => changeMonth("next")}
            className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center"
          >
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Week Days */}
        <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
          {weekDays.map((day) => (
            <div key={day} className="py-2 md:py-3 text-center text-xs md:text-sm font-bold text-slate-600">
              {day}
            </div>
          ))}
        </div>

        {/* Days Grid */}
        <div className="grid grid-cols-7 gap-1 md:gap-2 p-2 md:p-4 bg-white">
          {calendarDays.map((dateObj, index) => {
            if (!dateObj) return <div key={`empty-${index}`} className="aspect-square" />;
            const disabled = !dateObj.isAvailable;
            return (
              <button
                key={dateObj.gregorianDate}
                type="button"
                disabled={disabled}
                onClick={() => handleSelectDay(dateObj)}
                title={disabled ? getUnavailableReasonText(dateObj) : "قابل رزرو"}
                className={`
                  relative aspect-square rounded-xl md:rounded-2xl border transition-all duration-300 flex flex-col items-center justify-center gap-0.5
                  text-[12px] md:text-base font-black
                  ${
                    dateObj.isSelected
                      ? "border-purple-600 bg-purple-600 text-white shadow-lg shadow-purple-500/30 scale-105"
                      : dateObj.isAvailable
                      ? "border-green-200 bg-white hover:bg-green-50 hover:border-green-400 hover:shadow-md cursor-pointer"
                      : "border-slate-200 bg-slate-100 text-slate-400 opacity-60 cursor-not-allowed"
                  }
                `}
              >
                {dateObj.isToday && (
                  <span
                    className={`absolute top-1 right-1 w-2 h-2 rounded-full ${
                      dateObj.isSelected ? "bg-white" : "bg-blue-500"
                    }`}
                  />
                )}
                {disabled && (
                  <span className="absolute top-1 left-1">
                    <Lock className="w-3 h-3 md:w-3.5 md:h-3.5 text-slate-400" />
                  </span>
                )}
                {dateObj.isAvailable && !dateObj.isSelected && (
                  <span className="absolute top-1 left-1 hidden md:block">
                    <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                  </span>
                )}
                <span className="text-base md:text-lg">{dateObj.day}</span>
                {!dateObj.isAvailable && (
                  <span className="hidden md:inline text-[9px] md:text-[10px] px-0.5 text-center leading-4 line-clamp-2">
                    {getUnavailableReasonText(dateObj)}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* month navigation buttons */}
        <div className="flex items-center justify-between px-4 md:px-5 py-3 border-t border-slate-100 bg-slate-50/50">
          <button
            onClick={() => changeMonth("prev")}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 hover:bg-slate-100 hover:border-slate-300 transition-all text-sm font-medium shadow-sm"
          >
            <ArrowRight className="w-4 h-4" />
            <span>ماه قبل</span>
          </button>
          <button
            onClick={() => changeMonth("next")}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 hover:bg-slate-100 hover:border-slate-300 transition-all text-sm font-medium shadow-sm"
          >
            <span>ماه بعد</span>
            <ArrowLeft className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Time Selection */}
      {internalSelectedDate && (
        <div ref={timeSectionRef} className="space-y-4">
          <div className="flex items-center gap-3 bg-purple-50 border border-purple-200 rounded-2xl p-4">
            <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0" />
            <p className="text-sm font-semibold text-purple-800">
              تاریخ انتخاب‌شده: {internalSelectedDate.jalaaliDate}
            </p>
          </div>

          {fetchingDay ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
              <span className="mr-2 text-slate-600">دریافت ساعات...</span>
            </div>
          ) : dayDetail ? (
            augmentedTimeSlots.filter((s) => s.available).length > 0 ? (
              <div>
                <h4 className="text-sm font-bold text-slate-700 mb-3">انتخاب ساعت رزرو</h4>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                  {augmentedTimeSlots.map((slot) => {
                    const isDisabled = !slot.available;
                    const isSelected = selectedTime === slot.time;
                    if (isDisabled) {
                      return (
                        <div
                          key={slot.time}
                          className="relative rounded-xl border border-slate-200 bg-slate-100 p-2 text-center opacity-60 cursor-not-allowed select-none"
                        >
                          <span className="text-sm font-semibold text-slate-400 line-through">
                            {slot.time}
                          </span>
                          <XCircle className="absolute top-1 right-1 w-3 h-3 text-slate-400" />
                        </div>
                      );
                    }
                    return (
                      <button
                        key={slot.time}
                        type="button"
                        onClick={() => handleTimeSelect(slot.time)}
                        className={`relative rounded-xl border-2 p-2 text-center transition-all duration-200 ${
                          isSelected
                            ? "border-purple-600 bg-purple-50 shadow-md shadow-purple-200"
                            : "border-emerald-200 bg-white hover:border-emerald-400 hover:shadow-sm"
                        }`}
                      >
                        <span className={`text-sm font-bold ${isSelected ? "text-purple-700" : "text-slate-800"}`}>
                          {slot.time}
                        </span>
                        {isSelected && (
                          <span className="absolute -top-1.5 -right-1.5 bg-purple-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px]">
                            ✓
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

              
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-center">
                <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                <p className="text-sm text-amber-800 font-medium">
                  این روز تایم خالی ندارد. روز دیگری انتخاب کنید.
                </p>
              </div>
            )
          ) : (
            <div className="text-center text-slate-500 py-4">
              اطلاعات ساعتی برای این روز در دسترس نیست.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EditNormalDatePickerStep;
